import { addToolBreadcrumb } from '@tunasong/models'
import {
  METER_POOL,
  computeMeterSessionStats,
  computeSubdivisionSessionStats,
  generateMeterQuestion,
  generateSubdivisionQuestion,
  validateMeterAnswer,
  validateSubdivisionAnswer,
} from '@tunasong/music-lib'
import type {
  MeterKind,
  MeterQuestion,
  MeterSessionResult,
  MeterSpec,
  MeterVerdict,
  SubdivisionKind,
  SubdivisionQuestion,
  SubdivisionSessionResult,
  SubdivisionVerdict,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { useClickPlayback } from './click-playback.js'
import { MeterFeedback } from './meter-feedback.js'
import { MeterQuestionPanel } from './meter-question.js'
import { parseMeterParam, parseRhythmSubMode } from './rhythm-trainer-params.js'
import type { RhythmSubMode } from './rhythm-trainer-params.js'
import { RhythmTrainerSummary } from './rhythm-trainer-summary.js'
import { RhythmTrainerWelcome } from './rhythm-trainer-welcome.js'
import { SubdivisionFeedback } from './subdivision-feedback.js'
import { SubdivisionQuestionPanel } from './subdivision-question.js'
import styles from './rhythm-trainer.module.css'

const QUESTIONS_PER_SESSION = 10
const AUTOPLAY_DELAY_MS = 250
const TEMPO_BPM = 90
const AUDITION_MEASURES = 4

/**
 * 4/4 spec used by the Subdivision ID sub-mode. METER_POOL is a Bowie-locked
 * runtime constant; the find-or-throw pattern keeps `!` non-null assertions
 * out of the host while guarding against accidental pool reorders.
 */
const FOUR_FOUR_SPEC: MeterSpec = (() => {
  const entry = METER_POOL.find(e => e.kind === '4-4')
  if (!entry) {
    throw new Error('METER_POOL missing 4-4 entry — broken analyzer build')
  }
  return entry.spec
})()

type SessionState =
  | { phase: 'welcome'; preSelected: RhythmSubMode | null; preSelectedMeter: MeterKind | null }
  | {
      phase: 'asking-meter'
      index: number
      question: MeterQuestion
      results: MeterSessionResult[]
      focusedMeter: MeterKind | null
    }
  | {
      phase: 'feedback-meter'
      index: number
      question: MeterQuestion
      verdict: MeterVerdict
      results: MeterSessionResult[]
      focusedMeter: MeterKind | null
    }
  | { phase: 'done-meter'; results: MeterSessionResult[] }
  | {
      phase: 'asking-subdivision'
      index: number
      question: SubdivisionQuestion
      results: SubdivisionSessionResult[]
    }
  | {
      phase: 'feedback-subdivision'
      index: number
      question: SubdivisionQuestion
      verdict: SubdivisionVerdict
      results: SubdivisionSessionResult[]
    }
  | { phase: 'done-subdivision'; results: SubdivisionSessionResult[] }

/**
 * Rhythm Trainer v1 host. Two sub-modes (Meter ID, Subdivision ID); 10-question
 * sessions; locked tempo 90 BPM; 4-measure audition. Session state machine
 * mirrors Ear Training v1.5 (welcome → asking → feedback → done) cloned per
 * sub-mode.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §10
 */
export const RhythmTrainer: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => ({
    phase: 'welcome',
    preSelected: parseRhythmSubMode(searchParams.get('mode')),
    preSelectedMeter: parseMeterParam(searchParams.get('meter')),
  }))

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'rhythm-trainer' })
  }, [])

  // Active question for playback. A null payload yields an empty stab list
  // (no-op play), so hooks mount unconditionally regardless of phase.
  const meterQuestion: MeterQuestion | null =
    state.phase === 'asking-meter' || state.phase === 'feedback-meter' ? state.question : null
  const subQuestion: SubdivisionQuestion | null =
    state.phase === 'asking-subdivision' || state.phase === 'feedback-subdivision'
      ? state.question
      : null

  const { play: playMeter } = useClickPlayback({
    meter: meterQuestion ? meterQuestion.entry.spec : null,
    bpm: TEMPO_BPM,
    measures: AUDITION_MEASURES,
  })
  const { play: playSubdivision } = useClickPlayback({
    meter: subQuestion ? FOUR_FOUR_SPEC : null,
    subdivision: subQuestion?.entry.spec,
    bpm: TEMPO_BPM,
    measures: AUDITION_MEASURES,
  })

  // Autoplay on each new asking-* mount.
  const askingMeterQuestion = state.phase === 'asking-meter' ? state.question : null
  useEffect(() => {
    if (askingMeterQuestion === null) {
      return
    }
    const handle = window.setTimeout(playMeter, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingMeterQuestion, playMeter])

  const askingSubQuestion = state.phase === 'asking-subdivision' ? state.question : null
  useEffect(() => {
    if (askingSubQuestion === null) {
      return
    }
    const handle = window.setTimeout(playSubdivision, AUTOPLAY_DELAY_MS)
    return () => window.clearTimeout(handle)
  }, [askingSubQuestion, playSubdivision])

  const startMeter = (focusedMeter: MeterKind | null) => {
    const question = generateMeterQuestion(undefined, {
      focusedMeter: focusedMeter ?? undefined,
    })
    setState({ phase: 'asking-meter', index: 0, question, results: [], focusedMeter })
  }
  const startSubdivision = () => {
    setState({
      phase: 'asking-subdivision',
      index: 0,
      question: generateSubdivisionQuestion(),
      results: [],
    })
  }
  const start = (subMode: RhythmSubMode) => {
    if (subMode === 'meter') {
      startMeter(state.phase === 'welcome' ? state.preSelectedMeter : null)
      return
    }
    startSubdivision()
  }

  const answerMeter = (picked: MeterKind) => {
    if (state.phase !== 'asking-meter') {
      return
    }
    const verdict = validateMeterAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'rhythm-trainer', subMode: 'meter', correct: verdict.correct })
    setState({
      phase: 'feedback-meter',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
      focusedMeter: state.focusedMeter,
    })
  }
  const answerSubdivision = (picked: SubdivisionKind) => {
    if (state.phase !== 'asking-subdivision') {
      return
    }
    const verdict = validateSubdivisionAnswer(state.question, picked)
    addToolBreadcrumb('answered', { tool: 'rhythm-trainer', subMode: 'subdivision', correct: verdict.correct })
    setState({
      phase: 'feedback-subdivision',
      index: state.index,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const nextMeter = () => {
    if (state.phase !== 'feedback-meter') {
      return
    }
    const results: MeterSessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-meter', results })
      return
    }
    const question = generateMeterQuestion(undefined, {
      focusedMeter: state.focusedMeter ?? undefined,
    })
    setState({
      phase: 'asking-meter',
      index: state.index + 1,
      question,
      results,
      focusedMeter: state.focusedMeter,
    })
  }
  const nextSubdivision = () => {
    if (state.phase !== 'feedback-subdivision') {
      return
    }
    const results: SubdivisionSessionResult[] = [
      ...state.results,
      { question: state.question, verdict: state.verdict },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-subdivision', results })
      return
    }
    setState({
      phase: 'asking-subdivision',
      index: state.index + 1,
      question: generateSubdivisionQuestion(),
      results,
    })
  }

  const switchMode = (lastSubMode: RhythmSubMode) => {
    const nextMode: RhythmSubMode = lastSubMode === 'meter' ? 'subdivision' : 'meter'
    setState({ phase: 'welcome', preSelected: nextMode, preSelectedMeter: null })
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/app/ear-training', lexiconId: 'crosslink-rhythm-to-ear' },
    { targetRoute: '/app/quiz', lexiconId: 'crosslink-rhythm-to-quiz' },
    { targetRoute: '/app/modes', lexiconId: 'crosslink-rhythm-to-mode' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <RhythmTrainerWelcome preSelected={state.preSelected} onStart={start} />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking-meter') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <MeterQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playMeter}
          onAnswer={answerMeter}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-meter') {
    const scoreCorrect =
      state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <MeterQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playMeter}
          onAnswer={() => {
            /* feedback phase — disabled via interactive={false} */
          }}
        />
        <MeterFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playMeter}
          onNext={nextMeter}
        />
      </div>
    )
  }

  if (state.phase === 'asking-subdivision') {
    const scoreCorrect = state.results.filter(r => r.verdict.correct).length
    return (
      <div className={styles.container}>
        <SubdivisionQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          interactive={true}
          onHearIt={playSubdivision}
          onAnswer={answerSubdivision}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-subdivision') {
    const scoreCorrect =
      state.results.filter(r => r.verdict.correct).length + (state.verdict.correct ? 1 : 0)
    return (
      <div className={styles.container}>
        <SubdivisionQuestionPanel
          question={state.question}
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length + 1}
          interactive={false}
          onHearIt={playSubdivision}
          onAnswer={() => {
            /* feedback phase — disabled */
          }}
        />
        <SubdivisionFeedback
          question={state.question}
          verdict={state.verdict}
          onHearAgain={playSubdivision}
          onNext={nextSubdivision}
        />
      </div>
    )
  }

  if (state.phase === 'done-meter') {
    return (
      <div className={styles.container}>
        <RhythmTrainerSummary
          subMode="meter"
          stats={computeMeterSessionStats(state.results)}
          onRestart={() => startMeter(null)}
          onSwitchMode={switchMode}
        />
      </div>
    )
  }

  // state.phase === 'done-subdivision'
  return (
    <div className={styles.container}>
      <RhythmTrainerSummary
        subMode="subdivision"
        stats={computeSubdivisionSessionStats(state.results)}
        onRestart={startSubdivision}
        onSwitchMode={switchMode}
      />
    </div>
  )
}
